import { useState, useEffect, useRef, useCallback } from "react";
import axios from "axios";
import { API } from "../../App";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Badge } from "../ui/badge";
import { Switch } from "../ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { Loader2, Plus, X, ScanText, ImageOff, Check, Pencil, Monitor, ScanSearch } from "lucide-react";

const RULE_TYPES = [
  { value: "fixed",   label: "Fixed",   desc: "Exact match" },
  { value: "similar", label: "Similar", desc: "Fuzzy match (OCR noise tolerance)" },
  { value: "range",   label: "Range",   desc: "Min–max numeric tolerance" },
  { value: "dynamic", label: "Dynamic", desc: "Value entered by tester at test start" },
];

const EMPTY_FORM = {
  label: "",
  type: "fixed",
  extracted_values: [],
  value: "",
  min: "",
  max: "",
  unit: "",
};

const chipKey = (d) => `${d.text}__${d.confidence}`;

async function hashImage(base64) {
  const buf = await crypto.subtle.digest("SHA-256", new TextEncoder().encode(base64));
  return Array.from(new Uint8Array(buf)).map((b) => b.toString(16).padStart(2, "0")).join("");
}

const computeAutoValue = (extractedValues, type) => {
  if (type === "range") return null;
  if (extractedValues.length === 2) {
    return type === "similar" ? extractedValues[1].text : extractedValues.map((v) => v.text).join(" ");
  }
  if (extractedValues.length === 1) return extractedValues[0].text;
  return "";
};

export const OcrValidationConfig = ({ value, onChange, token, referenceImageBase64 }) => {
  const [detecting, setDetecting]     = useState(false);
  const [detected, setDetected]       = useState([]);
  const [detectError, setDetectError] = useState(null);
  const [screensFound, setScreensFound] = useState(null);
  const [form, setForm]               = useState(EMPTY_FORM);
  const [editingId, setEditingId]     = useState(null);
  const prevRefImage = useRef(null);

  const config = value || { enabled: false, rules: [] };
  const hasRef = Boolean(referenceImageBase64);
  const screenDetection = config.screen_detection !== false;
  const update = (patch) => onChange({ ...config, ...patch });

  const runDetection = useCallback(async (base64, useScreenDetection) => {
    setDetecting(true);
    setDetectError(null);
    setDetected([]);
    try {
      const res = await axios.post(
        `${API}/ocr/extract`,
        { image_base64: base64, screen_detection: useScreenDetection },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const lines = res.data.lines || [];
      setDetected(lines);
      setScreensFound(res.data.screens_found ?? null);
      return lines;
    } catch {
      setDetectError("OCR scan failed — make sure the backend is running.");
      return null;
    } finally {
      setDetecting(false);
    }
  }, [token]);

  // On open: restore cached chips if hash matches AND screen_detection mode matches.
  // Never auto-trigger a fresh scan — user must click Scan.
  useEffect(() => {
    if (!config.enabled || !referenceImageBase64) return;
    if (referenceImageBase64 === prevRefImage.current) return;
    prevRefImage.current = referenceImageBase64;

    (async () => {
      let hash = null;
      try { hash = await hashImage(referenceImageBase64); } catch { /* non-secure context */ }
      // hash is null in HTTP contexts (crypto.subtle unavailable) — skip hash check in that case
      const hashOk = !hash || config.detected_chips_hash === hash;
      // if detected_chips_screen is unset (old saved steps), treat as matching
      const modeMatches = config.detected_chips_screen == null || config.detected_chips_screen === screenDetection;
      if (hashOk && config.detected_chips?.length && modeMatches) {
        setDetected(config.detected_chips);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [config.enabled, referenceImageBase64]);

  // Clear cached chips when screen detection mode changes
  useEffect(() => {
    if (!config.enabled) return;
    const modeMatches = config.detected_chips_screen === screenDetection;
    if (!modeMatches && config.detected_chips?.length) {
      setDetected([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [screenDetection]);

  const handleScan = async () => {
    if (!referenceImageBase64) return;
    let hash = null;
    try { hash = await hashImage(referenceImageBase64); } catch { /* non-secure context */ }
    const lines = await runDetection(referenceImageBase64, screenDetection);
    if (lines && hash) {
      update({ detected_chips: lines, detected_chips_hash: hash, detected_chips_screen: screenDetection });
    }
  };

  const handleToggle = (enabled) => {
    update({ enabled });
    if (!enabled) { setDetected([]); prevRefImage.current = null; }
  };

  const MAX_CHIPS = 2;

  const isChipSelected = (d) => form.extracted_values.some((v) => chipKey(v) === chipKey(d));

  const toggleChip = (d) => {
    setForm((f) => {
      const key = chipKey(d);
      const selectedKeys = new Set(f.extracted_values.map(chipKey));
      if (selectedKeys.has(key)) {
        selectedKeys.delete(key);
      } else {
        if (selectedKeys.size >= MAX_CHIPS) return f;
        selectedKeys.add(key);
      }
      const newValues = detected
        .filter((det) => selectedKeys.has(chipKey(det)))
        .map(({ text, confidence, bbox }) => ({ text, confidence, bbox }));
      const autoValue = computeAutoValue(newValues, f.type);
      return {
        ...f,
        extracted_values: newValues,
        ...(autoValue !== null ? { value: autoValue } : {}),
      };
    });
  };

  const typeInfo = RULE_TYPES.find((t) => t.value === form.type);
  const hasValue = form.type === "range" ? (form.min !== "" || form.max !== "") : form.value.trim() !== "";
  const canAdd   = form.label.trim() && hasValue;

  const startEdit = (rule) => {
    setEditingId(rule.id);
    setForm({
      label:            rule.label,
      type:             rule.type,
      extracted_values: rule.extracted_values || [],
      value:            rule.value || "",
      min:              rule.min   || "",
      max:              rule.max   || "",
      unit:             rule.unit  || "",
    });
  };

  const cancelEdit = () => {
    setEditingId(null);
    setForm(EMPTY_FORM);
  };

  const commitRule = () => {
    if (!canAdd) return;
    const patch = {
      label:            form.label.trim(),
      type:             form.type,
      extracted_values: form.extracted_values,
      value:            form.value,
      min:              form.min,
      max:              form.max,
      unit:             form.unit,
    };
    if (editingId) {
      update({ rules: config.rules.map((r) => r.id === editingId ? { ...r, ...patch } : r) });
      setEditingId(null);
    } else {
      update({ rules: [...config.rules, { id: Math.random().toString(36).slice(2), ...patch }] });
    }
    setForm(EMPTY_FORM);
  };

  const removeRule = (id) => {
    if (editingId === id) cancelEdit();
    update({ rules: config.rules.filter((r) => r.id !== id) });
  };

  return (
    <div className="space-y-3 border rounded-lg p-4 bg-muted/20">

      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <ScanText className="w-4 h-4 text-blue-500" />
          <Label
            className={`font-semibold ${!hasRef ? "text-muted-foreground" : "cursor-pointer"}`}
            onClick={() => hasRef && handleToggle(!config.enabled)}
          >
            OCR Validation
          </Label>
        </div>
        <Switch checked={config.enabled} disabled={!hasRef} onCheckedChange={handleToggle} />
      </div>

      {!hasRef && !config.enabled && (
        <div className="flex items-center gap-2 text-xs text-muted-foreground py-1">
          <ImageOff className="w-3.5 h-3.5 shrink-0" />
          Upload a reference image above to enable OCR validation.
        </div>
      )}

      {config.enabled && (
        <div className="space-y-4 pt-1">

          {/* Screen detection toggle + scan button */}
          <div className="flex items-center justify-between rounded-lg border px-3 py-2 bg-background gap-3">
            <div className="flex items-center gap-2 flex-1">
              <Monitor className="w-3.5 h-3.5 text-blue-500 shrink-0" />
              <div>
                <p className="text-xs font-medium leading-none">Screen Detection</p>
                <p className="text-xs text-muted-foreground mt-0.5">
                  {screenDetection
                    ? "Auto-crops the instrument display region before reading"
                    : "Reads the full image — use for stickers & nameplates"}
                </p>
              </div>
            </div>
            <Switch
              checked={screenDetection}
              onCheckedChange={(checked) => update({ screen_detection: checked })}
            />
          </div>

          {/* Scan button + status */}
          {!hasRef && (
            <div className="flex items-center gap-2 text-xs text-muted-foreground py-1">
              <ImageOff className="w-3.5 h-3.5 shrink-0" />
              Re-upload a reference image above to re-scan for chips.
            </div>
          )}
          <div className="flex items-center gap-3">
            <Button
              type="button"
              variant="outline"
              size="sm"
              className="h-8 text-xs gap-1.5 shrink-0"
              disabled={detecting || !hasRef}
              onClick={handleScan}
            >
              {detecting
                ? <><Loader2 className="w-3.5 h-3.5 animate-spin" /> Scanning…</>
                : <><ScanSearch className="w-3.5 h-3.5" /> Scan Reference Image</>
              }
            </Button>
            {!detecting && detectError && (
              <span className="text-xs text-destructive">{detectError}</span>
            )}
            {!detecting && !detectError && detected.length > 0 && (
              <div className="flex items-center gap-2 flex-wrap">
                <span className="text-xs text-muted-foreground">
                  {detected.length} field{detected.length !== 1 ? "s" : ""} detected
                </span>
                {screensFound === null && (
                  <span className="text-xs bg-muted text-muted-foreground px-1.5 py-0.5 rounded">
                    Screen detection off
                  </span>
                )}
                {screensFound === 0 && (
                  <span className="text-xs bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400 px-1.5 py-0.5 rounded">
                    No screen detected · full image used
                  </span>
                )}
                {screensFound >= 1 && (
                  <span className="text-xs bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400 px-1.5 py-0.5 rounded">
                    {screensFound} screen{screensFound !== 1 ? "s" : ""} detected
                  </span>
                )}
              </div>
            )}
          </div>

          {/* ── Add / edit rule form ── */}
          <div className={`border rounded-lg p-3 bg-background space-y-3 transition-opacity ${!detected.length && !editingId && form.type !== "dynamic" ? "opacity-50 pointer-events-none" : ""} ${editingId ? "border-blue-400 ring-1 ring-blue-400" : ""}`}>

            {/* Row 1: field name + type */}
            <div className="flex gap-2">
              <Input
                placeholder="Field name — e.g. TURBIDITY"
                value={form.label}
                onChange={(e) => setForm({ ...form, label: e.target.value })}
                onKeyDown={(e) => e.key === "Enter" && commitRule()}
                className="h-8 text-xs font-mono flex-1"
              />
              <Select
                value={form.type}
                onValueChange={(v) => setForm((f) => ({
                  ...f,
                  type: v,
                  value: computeAutoValue(f.extracted_values, v) ?? "",
                  min: "",
                  max: "",
                            }))}
              >
                <SelectTrigger className="h-8 text-xs w-28 shrink-0">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {RULE_TYPES.map((t) => (
                    <SelectItem key={t.value} value={t.value} className="text-xs">{t.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Row 2: detected chips — hidden for dynamic (no reference value needed) */}
            {detected.length > 0 && form.type !== "dynamic" && (
              <div className="space-y-1.5">
                <p className="text-xs text-muted-foreground">
                  Select 1 field, or 2 adjacent fields read together as one value:
                </p>
                <div className="flex flex-wrap gap-1.5 max-h-40 overflow-y-auto p-0.5">
                  {detected.map((d, i) => {
                    const selected = isChipSelected(d);
                    const capReached = !selected && form.extracted_values.length >= MAX_CHIPS;
                    return (
                      <Badge
                        key={i}
                        variant={selected ? "default" : "outline"}
                        className={`font-mono text-xs transition-colors select-none gap-1 ${
                          capReached ? "opacity-40 cursor-not-allowed" : "cursor-pointer"
                        } ${
                          selected
                            ? "bg-blue-600 text-white hover:bg-blue-700 border-blue-600"
                            : !capReached && "hover:bg-blue-50 hover:border-blue-400 dark:hover:bg-blue-950"
                        }`}
                        onClick={() => !capReached && toggleChip(d)}
                      >
                        {selected && <Check className="w-3 h-3 shrink-0" />}
                        {d.text}
                        <span className={`opacity-70 ${selected ? "text-blue-100" : "text-muted-foreground"}`}>
                          {Math.round(d.confidence * 100)}%
                        </span>
                      </Badge>
                    );
                  })}
                </div>
                {form.extracted_values.length > 0 && (
                  <p className="text-xs text-muted-foreground">
                    {form.extracted_values.length} field{form.extracted_values.length !== 1 ? "s" : ""} selected:&nbsp;
                    <span className="font-mono text-foreground">
                      {form.extracted_values.map((v) => v.text).join(", ")}
                    </span>
                    {form.extracted_values.length === 2 && " — combined and matched as one value"}
                  </p>
                )}
              </div>
            )}

            {/* Row 3: expected value */}
            {form.type === "range" ? (
              <div className="space-y-2">
                <div className="flex items-center gap-2">
                  <Input
                    type="number"
                    placeholder="Min"
                    value={form.min}
                    onChange={(e) => setForm({ ...form, min: e.target.value })}
                    className="h-8 text-xs font-mono"
                  />
                  <span className="text-xs text-muted-foreground shrink-0">–</span>
                  <Input
                    type="number"
                    placeholder="Max"
                    value={form.max}
                    onChange={(e) => setForm({ ...form, max: e.target.value })}
                    className="h-8 text-xs font-mono"
                  />
                  <Input
                    placeholder="Unit (e.g. NTU)"
                    value={form.unit}
                    onChange={(e) => setForm({ ...form, unit: e.target.value })}
                    className="h-8 text-xs font-mono w-24 shrink-0"
                  />
                </div>
                <p className="text-xs text-muted-foreground">Decimal point is placed automatically based on the configured range.</p>
              </div>
            ) : (
              <Input
                placeholder={
                  form.type === "dynamic"  ? "Variable name — e.g. serial_no" :
                  form.type === "similar"  ? "Example value — e.g. AB-AXX-1232" :
                                            "Expected value — e.g. 500 NTU"
                }
                value={form.value}
                onChange={(e) => setForm({ ...form, value: e.target.value })}
                onKeyDown={(e) => e.key === "Enter" && commitRule()}
                className="h-8 text-xs font-mono"
              />
            )}
            {form.type === "dynamic" && (
              <p className="text-xs text-amber-600 dark:text-amber-400">
                Tester will be prompted to enter this value before starting the test.
              </p>
            )}

            <div className="flex items-center justify-between gap-2">
              <p className="text-xs text-muted-foreground">{typeInfo?.desc}</p>
              <div className="flex gap-2">
                {editingId && (
                  <Button type="button" variant="ghost" size="sm" className="h-7 px-3 text-xs" onClick={cancelEdit}>
                    Cancel
                  </Button>
                )}
                <Button type="button" size="sm" className="h-7 px-3 text-xs" disabled={!canAdd} onClick={commitRule}>
                  {editingId
                    ? <><Check className="w-3.5 h-3.5 mr-1" /> Save</>
                    : <><Plus className="w-3.5 h-3.5 mr-1" /> Add Rule</>
                  }
                </Button>
              </div>
            </div>
          </div>

          {/* Saved rules */}
          {config.rules.length > 0 && (
            <div className="space-y-1.5">
              <Label className="text-xs text-muted-foreground">Rules ({config.rules.length})</Label>
              {config.rules.map((rule) => (
                <div
                  key={rule.id}
                  className={`flex items-center gap-2 border rounded-lg px-3 py-2 bg-background ${editingId === rule.id ? "border-blue-400 bg-blue-50 dark:bg-blue-950/30" : ""}`}
                >
                  <div className="flex-1 min-w-0 space-y-0.5">
                    <span className="text-xs font-mono font-medium block truncate">{rule.label}</span>
                    {rule.extracted_values?.length > 0 && (
                      <span className="text-xs text-muted-foreground font-mono block truncate">
                        ref: {rule.extracted_values.map((v) => v.text).join(", ")}
                      </span>
                    )}
                  </div>
                  <Badge
                    variant="secondary"
                    className={`text-xs shrink-0 capitalize ${rule.type === "dynamic" ? "bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400" : ""}`}
                  >
                    {rule.type}
                  </Badge>
                  <span className="text-xs font-mono text-muted-foreground shrink-0">
                    {rule.type === "range"
                      ? `${rule.min} – ${rule.max}${rule.unit ? ` ${rule.unit}` : ""}`
                      : rule.type === "dynamic"
                      ? `{{${rule.value}}}`
                      : rule.value}
                  </span>
                  <Button
                    type="button" variant="ghost" size="icon"
                    className="h-6 w-6 shrink-0 text-muted-foreground hover:text-blue-600"
                    onClick={() => editingId === rule.id ? cancelEdit() : startEdit(rule)}
                  >
                    {editingId === rule.id ? <X className="w-3.5 h-3.5" /> : <Pencil className="w-3.5 h-3.5" />}
                  </Button>
                  <Button
                    type="button" variant="ghost" size="icon"
                    className="h-6 w-6 shrink-0 text-muted-foreground hover:text-destructive"
                    onClick={() => removeRule(rule.id)}
                  >
                    <X className="w-3.5 h-3.5" />
                  </Button>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};
